import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from '../api/index';
import { getToken, getUser } from '../api/auth';

export const useMessageStream = (activeConversationId = null) => {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef(null);
  const activeIdRef = useRef(null);

  useEffect(() => {
    activeIdRef.current =
      activeConversationId !== null && activeConversationId !== undefined
        ? String(activeConversationId)
        : null;
  }, [activeConversationId]);

  useEffect(() => {
    const token = getToken();
    const user = getUser();
    if (!token) {
      console.log('No token available, skipping message stream');
      return;
    }
    console.log('Setting up SSE connection to:', `${API_BASE_URL}/api/whatsapp/stream`);

    // Create EventSource connection with token as query parameter
    // Note: EventSource doesn't support custom headers, so we use query param
    const companyId = user?.companyId;
    if (!companyId) {
      console.log('No companyId available, skipping message stream');
      return;
    }
    const eventSource = new EventSource(
      `${API_BASE_URL}/api/whatsapp/stream?token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(
        String(companyId)
      )}`
    );

    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Ignore heartbeat messages
        if (data.type === 'heartbeat') {
          return;
        }

        // Handle connection confirmation
        if (data.type === 'connected') {
          console.log('SSE connection confirmed:', data.message);
          return;
        }

        // Inline edit of an existing message
        if (data.type === 'message_edited') {
          const wamid = data.wa_message_id;
          const newContent = data.content || '';
          const editedAt = data.edited_at;
          if (!wamid) return;
          queryClient.setQueriesData({ queryKey: ['conversation'], exact: false }, (old) => {
            if (!old || !Array.isArray(old.messages)) return old;
            let touched = false;
            const messages = old.messages.map((m) => {
              if (m.waMessageId === wamid) {
                touched = true;
                return {
                  ...m,
                  content: newContent,
                  editedAt,
                  originalContent: m.originalContent || m.content,
                };
              }
              return m;
            });
            return touched ? { ...old, messages } : old;
          });
          return;
        }

        // Delivery status update (sent / delivered / read / failed)
        if (data.type === 'message_status') {
          const wamid = data.wa_message_id;
          const status = data.status;
          const conversationId = data.conversation_id;
          if (!wamid || !status) return;

          queryClient.setQueriesData({ queryKey: ['conversation'], exact: false }, (old) => {
            if (!old || !Array.isArray(old.messages)) return old;
            let touched = false;
            const messages = old.messages.map((m) => {
              if (m.waMessageId === wamid) {
                touched = true;
                return { ...m, status };
              }
              return m;
            });
            return touched ? { ...old, messages } : old;
          });
          return;
        }

        // Handle new message
        if (data.type === 'new_message') {
          const conversationId = data.conversation_id || data.sender;
          const timestamp = data.timestamp || new Date().toISOString();
          const role = data.role;
          const messageContent = data.message || data.caption || '';

          const messageType = data.message_type || (data.media_id
            ? (data.mime_type?.startsWith('image/') ? 'image'
              : data.mime_type?.startsWith('video/') ? 'video'
              : data.mime_type?.startsWith('audio/') ? 'audio'
              : 'document')
            : (data.interactive && Array.isArray(data.interactive.options) ? 'interactive' : 'text'));

          const interactive =
            data.interactive && Array.isArray(data.interactive.options)
              ? {
                  type: data.interactive.type || 'button',
                  bodyText: data.interactive.body_text || '',
                  options: data.interactive.options,
                }
              : null;

          const message = {
            id: data.wa_message_id || data.message_id || `${conversationId}-${timestamp}-${String(messageContent).slice(0, 10)}`,
            type: role === 'assistant' ? 'agent' : role === 'system' ? 'system' : 'customer',
            messageType,
            content: messageContent,
            caption: data.caption || null,
            timestamp,
            agent: role === 'assistant' ? 'Agent' : undefined,
            status: null,
            mediaId: data.media_id || null,
            mimeType: data.mime_type || null,
            filename: data.filename || null,
            voice: !!data.voice,
            durationS: data.duration_s ?? null,
            waMessageId: data.wa_message_id || null,
            replyToId: data.reply_to_id || null,
            isInternal: !!data.is_internal,
            authorUserId: data.author_user_id || null,
            authorName: data.author_name || null,
            location:
              data.lat != null && data.lng != null
                ? { lat: data.lat, lng: data.lng, name: data.name, address: data.address }
                : null,
            contacts: Array.isArray(data.contacts) ? data.contacts : null,
            interactive,
          };

          // 1) Update the open conversation transcript incrementally (append if not present)
          queryClient.setQueryData(['conversation', conversationId], (old) => {
            if (!old) return old;
            const prevMessages = Array.isArray(old.messages) ? old.messages : [];
            if (prevMessages.some((m) => m.id === message.id)) return old;
            return {
              ...old,
              messages: [...prevMessages, message].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
              lastMessage: message.content,
              lastMessageTime: message.timestamp,
            };
          });

          // 2) Update any cached conversation lists (preview + ordering + unread)
          queryClient.setQueriesData({ queryKey: ['conversations'], exact: false }, (old) => {
            if (!old || !Array.isArray(old.data)) return old;
            const list = old.data;

            const existingIdx = list.findIndex((c) => c.id === conversationId || c.phone === conversationId);
            const existing = existingIdx >= 0 ? list[existingIdx] : null;

            const previewByType = {
              image: '📷 Foto',
              video: '🎥 Video',
              audio: data.voice ? '🎤 Nota de voz' : '🔊 Audio',
              document: data.filename ? `📎 ${data.filename}` : '📎 Documento',
              location: '📍 Ubicación',
              contacts: '👤 Contacto',
              interactive: interactive?.bodyText ? `☰ ${interactive.bodyText}` : '☰ Menú',
            };
            const previewText =
              messageContent ||
              previewByType[messageType] ||
              '';

            const updatedConv = {
              ...(existing || { id: conversationId, phone: conversationId, customerName: conversationId }),
              id: existing?.id || conversationId,
              phone: existing?.phone || conversationId,
              lastMessage: previewText,
              lastMessageTime: message.timestamp,
              // Increment unread count only for customer messages, and only
              // when the operator is not currently viewing this conversation.
              unreadCount:
                role === 'user' && activeIdRef.current !== String(conversationId)
                  ? Math.min((existing?.unreadCount || 0) + 1, 999)
                  : role === 'user' && activeIdRef.current === String(conversationId)
                    ? 0
                    : existing?.unreadCount || 0,
            };

            const next = existingIdx >= 0 ? [...list.slice(0, existingIdx), ...list.slice(existingIdx + 1)] : [...list];
            // Move to top
            next.unshift(updatedConv);

            return {
              ...old,
              data: next,
              total: typeof old.total === 'number' ? old.total : next.length,
            };
          });
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error, 'Raw data:', event.data);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      console.log('EventSource readyState:', eventSource.readyState);
      // 0 = CONNECTING, 1 = OPEN, 2 = CLOSED
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('SSE connection closed. Will attempt to reconnect...');
      }
    };

    eventSource.onopen = () => {
      console.log('SSE connection opened successfully');
    };

    return () => {
      if (eventSourceRef.current) {
        console.log('Closing SSE connection');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [queryClient]);
};

